import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { Seo, SITE_URL } from "../components/Seo";
import { LeadCaptureForm } from "../components/LeadCaptureForm";
import { Plane, Gamepad2, ShieldCheck, BadgeCheck, MessageSquareText, Trophy, ArrowRight, Check, Clock, Sparkles } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/edd81dd3-3914-4b9d-83c5-d38aa8b3ac0f/images/8cd5bec68cfdc1c0e63de688f3c7a33001c9a659a5248908d1335a603bba77ba.jpeg";

const features = [
  { icon: Plane, title: "Structured Courses", desc: "From drone anatomy to advanced maneuvers, learn step by step." },
  { icon: Gamepad2, title: "Flight Controls", desc: "Master throttle, yaw, pitch & roll with guided drills." },
  { icon: ShieldCheck, title: "Safety First", desc: "Pre-flight checklists, weather and emergency procedures." },
  { icon: BadgeCheck, title: "FAA Part 107 Prep", desc: "Everything to pass the Remote Pilot knowledge exam." },
  { icon: MessageSquareText, title: "AI Instructor", desc: "Ask Skyler anything, 24/7 — your personal flight coach." },
  { icon: Trophy, title: "Badges & Progress", desc: "Track flight hours, quiz scores and earn achievements." },
];

export default function Landing() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (ready && user) nav("/dashboard");
  }, [ready, user, nav]);

  return (
    <div className="min-h-screen">
      <Seo
        title="DronAcademy — Learn to Fly Drones Online & Pass FAA Part 107"
        description="Learn to fly drones online with interactive lessons, an AI flight instructor and full FAA Part 107 exam prep. From first hover to certified commercial pilot. Start free."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "DronAcademy",
            url: SITE_URL,
            description: "Online drone flight school with interactive lessons, an AI instructor and FAA Part 107 exam prep.",
          },
          {
            "@context": "https://schema.org",
            "@type": "Course",
            name: "DronAcademy — Full Drone Flying Course",
            description: "Interactive drone flight training covering basics, controls, safety and FAA Part 107 certification.",
            provider: { "@type": "Organization", name: "DronAcademy", sameAs: SITE_URL },
            offers: { "@type": "Offer", price: "89.00", priceCurrency: "USD", url: `${SITE_URL}/register` },
          },
        ]}
      />
      <header className="glass border-b border-border/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-sm bg-primary flex items-center justify-center glow-cyan">
              <Plane className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-lg">DRON<span className="text-primary">ACADEMY</span></span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" data-testid="landing-login-btn">Log in</Button></Link>
            <Link to="/register"><Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" data-testid="landing-signup-btn">Get Started</Button></Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Professional drone banking against a bright blue sky" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 to-background/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32 md:pt-32 md:pb-40">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-primary mb-5">
              <Sparkles className="h-3.5 w-3.5" /> Now enrolling · Ground School
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-4">
              Learn to fly like a <span className="text-primary">certified pilot</span>.
            </h1>
            <p className="text-base md:text-lg text-slate-200 mb-3 max-w-xl leading-relaxed">
              Start free with the introductory lessons. Full Ground School is a one-time $89 enrollment — enroll anytime.
            </p>
            <p className="text-sm md:text-base text-slate-400 mb-5 max-w-xl leading-relaxed">
              Learn to fly drones like a certified pilot with interactive lessons, practical missions, and FAA-aligned instruction.
            </p>
            <ul className="space-y-2 mb-4 max-w-xl" data-testid="course-benefits">
              {[
                "Interactive lessons with real-world practical missions",
                "FAA-aligned Part 107 knowledge and decision-making",
                "Progress tracking, badges, and Mission Checks",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm md:text-[15px] text-slate-200">
                  <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={3} /> {b}
                </li>
              ))}
            </ul>
            <p className="text-sm text-slate-400 max-w-xl mb-7">
              Create a free account and start your first lesson in minutes.
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <Link to="/register">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-12 px-7 text-base font-semibold group" data-testid="hero-cta-btn">
                  Start Flying Free
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="h-12 px-7 text-base rounded-sm border-border hover:bg-secondary" data-testid="hero-login-btn">
                  Log In
                </Button>
              </Link>
            </div>
            <p className="text-xs text-muted-foreground mt-3 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="hero-reassurance">
              <span>✓ Free account</span>
              <span>✓ No credit card required</span>
              <span>✓ First lesson takes about 9 minutes</span>
            </p>

            <div className="mt-7 max-w-md rounded-sm border border-primary/30 bg-primary/5 p-5" data-testid="hero-first-lesson-box">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">In the next 9 minutes you'll:</span>
              </div>
              <ul className="space-y-2">
                {[
                  "Learn how professional drone pilots think",
                  "Understand the mindset behind safe flying",
                  "Complete your first mission",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-slate-200">
                    <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={3} /> {t}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-3">No credit card required.</p>
            </div>

            <p className="text-[11px] text-muted-foreground/80 mt-5 max-w-xl leading-relaxed" data-testid="access-scope-note">
              Start free with the introductory lessons. Full Ground School is a one-time $89 enrollment that stays available for as long as DronAcademy operates; future Ground School lessons are included as they're released. Other academies, coaching, and live events are separate and not included.
            </p>

            <div className="flex gap-8 mt-8">
              {[["9", "Ground School Modules"], ["69", "Lessons"], ["Part 107", "Exam Prep"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-mono text-2xl font-bold text-primary">{n}</div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{l}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground/70 mt-3" data-testid="more-academies-note">
              More academies coming soon.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="border-t border-border/60 bg-secondary/10">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent">Your first lesson</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 mb-5">Mission One: Think like a pilot</h2>
            <ul className="space-y-3 mb-7">
              {[
                [Clock, "About 9 minutes"],
                [BadgeCheck, "Beginner friendly — no experience needed"],
                [Plane, "Learn how professional pilots think before takeoff"],
              ].map(([Icon, t]) => (
                <li key={t} className="flex items-center gap-3 text-[15px] text-slate-200">
                  <span className="h-9 w-9 rounded-sm bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-primary" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
            <Link to="/register">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-12 px-7 text-base font-semibold group" data-testid="first-lesson-cta">
                Start Lesson 1
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
          <div className="rounded-sm border border-primary/30 bg-primary/5 p-8">
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-primary mb-4">
              <Sparkles className="h-3.5 w-3.5" /> Ground School Academy · v1.0
            </span>
            <p className="text-lg text-slate-100 leading-relaxed font-display font-bold mb-3">
              "Good pilots don't rush decisions."
            </p>
            <p className="text-sm text-slate-400 leading-relaxed">
              Professionally reviewed, tested, and continuously improved. Your first five lessons build the mindset every professional pilot starts with.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 md:py-28">
        <div className="mb-14 max-w-xl">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent">The Curriculum</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">Everything you need to command the sky</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.5 }}
              className="group p-7 rounded-sm border border-border/70 bg-card hover:border-primary/50 transition-colors"
              data-testid={`feature-card-${i}`}
            >
              <div className="h-11 w-11 rounded-sm bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors">
                <f.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-display font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-12 items-center border-t border-border/60" data-testid="checklist-section">
        <div>
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent">Free download</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 mb-4">Not ready to start today?</h2>
          <p className="text-slate-400 leading-relaxed max-w-md">
            No problem. Download our free FAA Part 107 study checklist and we'll email you a copy — print it or save it as a PDF. When you're ready, your first lesson is waiting.
          </p>
        </div>
        <LeadCaptureForm
          source="home"
          heading="Get the free FAA Part 107 checklist"
          subheading="Enter your email and we'll send it over. No account needed."
        />
      </section>

      <footer className="border-t border-border/60 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-6">
          <div>
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Popular guides</span>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3">
              <Link to="/learn/faa-part-107-course" className="text-sm text-slate-400 hover:text-primary" data-testid="footer-seo-part107">FAA Part 107 Course</Link>
              <Link to="/learn/how-to-fly-a-drone" className="text-sm text-slate-400 hover:text-primary" data-testid="footer-seo-howto">How to Fly a Drone</Link>
              <Link to="/learn/learn-drone-flying-online" className="text-sm text-slate-400 hover:text-primary" data-testid="footer-seo-online">Learn Drone Flying Online</Link>
              <Link to="/learn/drone-training-for-beginners" className="text-sm text-slate-400 hover:text-primary" data-testid="footer-seo-beginners">Drone Training for Beginners</Link>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm text-muted-foreground border-t border-border/50 pt-6">
            <span className="font-mono">DRONACADEMY © 2026</span>
            <span>Fly smart. Fly legal.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
